import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useTheme } from 'next-themes';
import { useEffect, useRef, useState } from 'react';

import { BaseModal } from '@/components/ui/BaseModal';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/useToast';
import { useCreateSetupIntent } from '@/lib/query/useCreateSetupIntent';
import { useUpdatePaymentMethod } from '@/lib/query/useUpdatePaymentMethod';

const stripePromise = loadStripe(
  import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || ''
);

interface SubscribeButtonProps {
  variant?: 'default' | 'outline' | 'secondary' | 'ghost' | 'link';
  className?: string;
  wasPreviouslySubscribed?: boolean;
}

function SubscriptionForm({ onClose }: { onClose: () => void }) {
  const stripe = useStripe();
  const elements = useElements();
  const updatePaymentMethod = useUpdatePaymentMethod();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      // The server creates a SetupIntent to collect the card; confirm it, then
      // attach the resulting payment method so the trialing subscription charges.
      const { error: submitError, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/billing`,
        },
        redirect: 'if_required',
      });

      if (submitError) {
        setError(submitError.message || 'An error occurred');
        setIsProcessing(false);
        return;
      }

      if (setupIntent?.payment_method) {
        await updatePaymentMethod.mutateAsync(
          setupIntent.payment_method as string
        );
      }

      // Success - reload page to refresh subscription data
      toast.success(
        'Subscription activated!',
        'Your subscription is now active.'
      );
      window.location.reload();
    } catch (err) {
      const error = err instanceof Error ? err.message : 'An error occurred';
      setError(error);
      toast.error('Subscription failed', error);
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement />
      {error && <div className="text-destructive text-sm">{error}</div>}
      <div className="flex flex-col justify-center gap-3 sm:flex-row sm:justify-end">
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" disabled={!stripe || isProcessing}>
          {isProcessing ? 'Processing...' : 'Subscribe'}
        </Button>
      </div>
    </form>
  );
}

function SubscribeModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const { resolvedTheme } = useTheme();
  const createSetupIntent = useCreateSetupIntent();
  const { mutate: mutateSetupIntent, reset: resetSetupIntent } =
    createSetupIntent;
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const hasAttemptedRef = useRef(false);

  // Reset the setup-intent fetch state whenever the modal closes, so a user
  // who hit an error (e.g. 403 admin gate, network blip) gets a fresh retry
  // on the next open instead of being stuck on the error state until refresh.
  useEffect(() => {
    if (!isOpen) {
      setClientSecret(null);
      hasAttemptedRef.current = false;
      resetSetupIntent();
    }
  }, [isOpen, resetSetupIntent]);

  // Create the setup intent when modal opens (only once per open)
  useEffect(() => {
    if (!isOpen || clientSecret || hasAttemptedRef.current) {
      return;
    }

    hasAttemptedRef.current = true;
    mutateSetupIntent(undefined, {
      onSuccess: (data) => {
        setClientSecret(data.clientSecret);
      },
      onError: (error) => {
        toast.error(
          'Failed to initialize',
          error instanceof Error ? error.message : 'Please try again.'
        );
      },
    });
  }, [clientSecret, mutateSetupIntent, isOpen]);

  const appearance = {
    theme: (resolvedTheme === 'dark' ? 'night' : 'stripe') as
      | 'night'
      | 'stripe',
    variables: {
      colorPrimary: '#9b5eee',
    },
  };

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} title="Subscribe">
      {createSetupIntent.isPending && (
        <div className="text-muted-foreground py-8 text-center">Loading...</div>
      )}
      {createSetupIntent.isError && (
        <div className="text-destructive py-4">
          Failed to initialize payment form. Please try again.
        </div>
      )}
      {clientSecret && (
        <Elements stripe={stripePromise} options={{ clientSecret, appearance }}>
          <SubscriptionForm onClose={onClose} />
        </Elements>
      )}
    </BaseModal>
  );
}

export function SubscribeButton({
  variant = 'default',
  className,
  wasPreviouslySubscribed = false,
}: SubscribeButtonProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <>
      <Button
        variant={variant}
        className={className}
        onClick={() => setIsModalOpen(true)}
      >
        {wasPreviouslySubscribed ? 'Resubscribe' : 'Subscribe Now'}
      </Button>
      <SubscribeModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </>
  );
}
